import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAuthToken, getAuthToken } from "@/lib/api";
import { registerPushNotifications } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");

  // Register push token with backend once the user is authenticated (native only).
  useEffect(() => {
    if (!user || typeof user !== "object" || !user.id) return;
    let cancelled = false;
    (async () => {
      const platform = (await import("@capacitor/core")).Capacitor.getPlatform();
      if (platform === "web") return;
      await registerPushNotifications(async (token) => {
        if (cancelled) return;
        try {
          await api.post("/devices/register", { token, platform });
        } catch {
          // backend may be offline; silent
        }
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const fetchMe = useCallback(async () => {
    if (!getAuthToken()) {
      // Try cookie-based session anyway (works when 3rd-party cookies allowed)
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch {
        setUser(false);
        return;
      }
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setAuthToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail === "verification_required") {
        setError("");
        return { ok: false, verificationRequired: true, email };
      }
      setError(formatApiError(detail) || e.message);
      return { ok: false };
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      if (data.verification_required) {
        return { ok: true, verificationRequired: true, email };
      }
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthToken(null);
    setUser(false);
  };

  const updateSettings = async (patch) => {
    const { data } = await api.patch("/settings", patch);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, error, login, register, logout, updateSettings, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
